import { message } from "antd";
import _ from "lodash";

/**
 * CheckoutValidator component
 *
 * Props:
 * - billData: object - complete bill data including bill, items, payments, charges
 * - totalDue: number - total amount due
 * - totalPayments: number - total payments made
 * - onValidationPass: function - callback when validation passes
 * - onValidationFail: function - callback when validation fails (optional)
 */
export const CheckoutValidator = ({
  billData,
  totalDue,
  totalPayments,
  onValidationPass,
  onValidationFail,
}) => {
  const validateCheckout = () => {
    const errors = [];

    // Check for member/patient
    if (_.isEmpty(billData.bill.patientId)) {
      errors.push("Member not selected");
    }

    // Check for line items
    if (billData.items.length === 0) {
      errors.push("No services detected");
    }

    // Check for payment methods
    if (billData.payments.length === 0) {
      errors.push("No payments found");
    }

    // Check for customer if there is balance
    if (totalPayments < totalDue && !billData.bill.patientId) {
      errors.push("A member is required for incomplete payments");
    }

    // Check for valid quantities
    if (billData.items.filter((item) => !item.quantity).length > 0) {
      errors.push("Invalid service quantities");
    }

    // Check for valid services
    if (billData.items.filter((item) => !item.serviceId).length > 0) {
      errors.push("Empty service(s) not allowed");
    }

    // Check for valid payment methods
    if (billData.payments.filter((pay) => !pay.paymentMethod).length > 0) {
      errors.push("Invalid payment method(s)");
    }

    // Check for valid payment amounts
    if (
      billData.payments.filter((pay) => !pay.amount || pay.amount <= 0).length >
      0
    ) {
      errors.push("Invalid payment amounts");
    }

    // Check for redeemed benefits validation (if applicable)
    if (billData.redeemedBenefits && billData.redeemedBenefits.length > 0) {
      const totalRedeemedAmount = billData.redeemedBenefits.reduce(
        (sum, benefit) => sum + parseFloat(benefit.amount || 0),
        0
      );

      if (totalRedeemedAmount > totalDue) {
        errors.push("Redeemed benefits exceed total bill amount");
      }
    }

    // If there are errors, show them and call onValidationFail
    if (errors.length > 0) {
      errors.forEach((error) => message.error(error));
      if (onValidationFail) {
        onValidationFail(errors);
      }
      return false;
    }

    // Validation passed, call onValidationPass
    onValidationPass();
    return true;
  };

  return { validateCheckout };
};
